import { useState, useMemo } from "react";
import {
  Search,
  Download,
  MoreVertical,
  Key,
  Shield,
  Trash2,
  Ban,
  CheckCircle,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import type { UserAccessItem } from "../../../api/accessApi";
import {
  updateAccessUserStatus,
  updateAccessUserRole,
  resetAccessUserPassword,
  deleteAccessUser,
} from "../../../api/accessApi";

interface UsersTableProps {
  users: UserAccessItem[];
  total: number;
  isLoading: boolean;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  search: string;
  setSearch: (search: string) => void;
  roleFilter: string;
  setRoleFilter: (role: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  planFilter: string;
  setPlanFilter: (plan: string) => void;
  sortField: string;
  setSortField: (field: string) => void;
  sortDirection: "asc" | "desc";
  setSortDirection: (dir: "asc" | "desc") => void;
  onManageUser: (user: UserAccessItem) => void;
  onRefresh: () => void;
  currentAdminEmail?: string;
}

const ROLE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  super_admin: { bg: "rgba(245,158,11,0.15)", text: "#f59e0b", border: "rgba(245,158,11,0.3)" },
  admin: { bg: "rgba(239,68,68,0.12)", text: "#ef4444", border: "rgba(239,68,68,0.25)" },
  analyst: { bg: "rgba(168,85,247,0.12)", text: "#a855f7", border: "rgba(168,85,247,0.25)" },
  client: { bg: "rgba(0,102,224,0.12)", text: "#818cf8", border: "rgba(0,102,224,0.25)" },
};

function initials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

export default function UsersTable({
  users,
  total,
  isLoading,
  page,
  setPage,
  pageSize,
  setPageSize,
  search,
  setSearch,
  roleFilter,
  setRoleFilter,
  statusFilter,
  setStatusFilter,
  planFilter,
  setPlanFilter,
  sortField,
  setSortField,
  sortDirection,
  setSortDirection,
  onManageUser,
  onRefresh,
  currentAdminEmail,
}: UsersTableProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeKebabId, setActiveKebabId] = useState<string | null>(null);

  const toggleSelectAll = () => {
    if (selectedIds.length === users.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(users.map((u) => u.id));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // CSV Export
  const handleExportCsv = (itemsToExport = users) => {
    if (itemsToExport.length === 0) {
      toast.error("No items to export");
      return;
    }

    const headers = [
      "ID",
      "Name",
      "Email",
      "Role",
      "Status",
      "Plan Tier",
      "Users Count",
      "Assets Count",
      "Joined Date",
      "First Access",
      "Last Login",
    ];

    const rows = itemsToExport.map((u) => [
      u.id,
      `"${u.name.replace(/"/g, '""')}"`,
      u.email,
      u.role,
      u.status,
      u.plan || "free",
      u.users_count,
      u.assets_count,
      new Date(u.created_at).toLocaleDateString(),
      u.first_access_at ? new Date(u.first_access_at).toLocaleDateString() : "Never",
      u.last_login ? new Date(u.last_login).toLocaleDateString() : "Never",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `threatactix_access_directory_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${itemsToExport.length} accounts to CSV`);
  };

  // Bulk Actions
  const handleBulkStatusChange = async (newStatus: "active" | "suspended") => {
    if (selectedIds.length === 0) return;
    try {
      let updatedCount = 0;
      for (const id of selectedIds) {
        const u = users.find((x) => x.id === id);
        if (u && !u.is_protected && u.email.toLowerCase() !== currentAdminEmail?.toLowerCase()) {
          await updateAccessUserStatus(id, newStatus);
          updatedCount++;
        }
      }
      toast.success(`Bulk updated ${updatedCount} users to ${newStatus}`);
      setSelectedIds([]);
      onRefresh();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Bulk action failed");
    }
  };

  // Single Actions
  const handleToggleStatus = async (user: UserAccessItem) => {
    if (user.is_protected) {
      toast.error("Protected Super Admin accounts cannot be suspended");
      return;
    }
    if (user.email.toLowerCase() === currentAdminEmail?.toLowerCase()) {
      toast.error("You cannot suspend your own account");
      return;
    }
    const newStatus = user.status === "active" ? "suspended" : "active";
    try {
      await updateAccessUserStatus(user.id, newStatus);
      toast.success(`User ${user.name} is now ${newStatus}`);
      onRefresh();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update status");
    }
  };

  const handleRoleToggle = async (user: UserAccessItem) => {
    if (user.is_protected) {
      toast.error("Protected Super Admin accounts cannot be demoted or changed");
      return;
    }
    if (user.email.toLowerCase() === currentAdminEmail?.toLowerCase()) {
      toast.error("You cannot change your own role");
      return;
    }
    const newRole = user.role === "admin" ? "analyst" : "admin";
    try {
      await updateAccessUserRole(user.id, newRole);
      toast.success(`User ${user.name} updated to ${newRole.toUpperCase()}`);
      onRefresh();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update role");
    }
  };

  const handleResetPassword = async (user: UserAccessItem) => {
    const newPass = prompt(`Enter new password for ${user.email}:`, "Threatactix2026!");
    if (!newPass) return;
    try {
      await resetAccessUserPassword(user.id, newPass);
      toast.success(`Password for ${user.email} reset successfully`);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Password reset failed");
    }
  };

  const handleDelete = async (user: UserAccessItem) => {
    if (user.is_protected) {
      toast.error("Protected Super Admin accounts cannot be deleted");
      return;
    }
    if (user.email.toLowerCase() === currentAdminEmail?.toLowerCase()) {
      toast.error("You cannot delete your own account");
      return;
    }

    if (!confirm(`Are you sure you want to permanently delete user ${user.name} (${user.email})?`)) {
      return;
    }

    const targetId = user.id || user._id;
    if (!targetId) {
      toast.error("Invalid user identifier");
      return;
    }

    try {
      await deleteAccessUser(targetId);
      toast.success(`User ${user.name} deleted successfully`);
      onRefresh();
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || "Failed to delete user";
      toast.error(errorMsg);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="access-table-card">
      {/* Toolbar */}
      <div className="table-toolbar">
        <div className="toolbar-search">
          <Search className="search-icon-pos" size={16} />
          <input
            type="text"
            placeholder="Search users by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="toolbar-filters">
          <select
            className="select-filter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="client">Client</option>
            <option value="analyst">Analyst</option>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>

          <select
            className="select-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending Invite</option>
          </select>

          <select
            className="select-filter"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="all">All Plans</option>
            <option value="free">Free</option>
            <option value="Business Pro">Business Pro</option>
            <option value="Enterprise">Enterprise</option>
          </select>

          <span className="registered-count-badge">{total} accounts</span>

          <button className="btn-secondary" onClick={() => handleExportCsv()}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="bulk-action-bar">
          <span>{selectedIds.length} accounts selected</span>
          <div className="bulk-actions-group">
            <button className="btn-bulk btn-bulk-activate" onClick={() => handleBulkStatusChange("active")}>
              Activate
            </button>
            <button className="btn-bulk btn-bulk-suspend" onClick={() => handleBulkStatusChange("suspended")}>
              Suspend
            </button>
            <button
              className="btn-bulk btn-secondary"
              onClick={() => {
                const selectedUsers = users.filter((u) => selectedIds.includes(u.id));
                handleExportCsv(selectedUsers);
              }}
            >
              Export Selected
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="access-table-scroll">
        <table className="access-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={users.length > 0 && selectedIds.length === users.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Username & Email</th>
              <th>Role</th>
              <th>Plan Tier</th>
              <th>Users</th>
              <th>Assets</th>
              <th>Status</th>
              <th>Joined Date</th>
              <th>Last Login</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  <td colSpan={10} style={{ padding: "16px" }}>
                    <div className="skeleton" style={{ height: 24, width: "100%" }} />
                  </td>
                </tr>
              ))
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)" }}>
                  No accounts found matching your filter criteria.
                </td>
              </tr>
            ) : (
              users.map((item) => {
                const roleKey = item.role?.toLowerCase() || "client";
                const roleStyle = ROLE_COLORS[roleKey] || ROLE_COLORS.client;
                const isSelf = item.email.toLowerCase() === currentAdminEmail?.toLowerCase();

                return (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelect(item.id)}
                      />
                    </td>
                    <td>
                      <div className="user-cell">
                        <div
                          className="user-avatar"
                          style={{
                            background: roleStyle.bg,
                            color: roleStyle.text,
                          }}
                        >
                          {initials(item.name)}
                        </div>
                        <div className="user-info">
                          <div className="user-name-wrap">
                            <span className="user-name">{item.name}</span>
                            {item.is_protected && <span className="protected-badge">PROTECTED</span>}
                            {item.has_custom_overrides && <span className="custom-perms-badge">custom perms</span>}
                          </div>
                          <span className="user-email">{item.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          padding: "3px 8px",
                          borderRadius: 4,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          background: roleStyle.bg,
                          color: roleStyle.text,
                          border: `1px solid ${roleStyle.border}`,
                        }}
                      >
                        {item.role.replace("_", " ")}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          padding: "3px 8px",
                          borderRadius: 4,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          background:
                            item.plan === "Enterprise"
                              ? "rgba(168, 85, 247, 0.15)"
                              : item.plan === "Business Pro"
                              ? "rgba(99, 102, 241, 0.15)"
                              : "rgba(148, 163, 184, 0.15)",
                          color:
                            item.plan === "Enterprise"
                              ? "#c084fc"
                              : item.plan === "Business Pro"
                              ? "#818cf8"
                              : "#94a3b8",
                          border: `1px solid ${
                            item.plan === "Enterprise"
                              ? "rgba(168, 85, 247, 0.3)"
                              : item.plan === "Business Pro"
                              ? "rgba(99, 102, 241, 0.3)"
                              : "rgba(148, 163, 184, 0.2)"
                          }`,
                        }}
                      >
                        {item.plan || "Free"}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{item.users_count ?? 0}</span>
                    </td>
                    <td>
                      <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{item.assets_count ?? 0}</span>
                    </td>
                    <td>
                      <span className={`status-badge status-${item.status}`}>
                        <span className="status-dot-indicator" />
                        {item.status === "active" ? "Active" : item.status === "suspended" ? "Suspended" : "Pending"}
                      </span>
                    </td>
                    <td style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                      {new Date(item.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                      {item.last_login ? new Date(item.last_login).toLocaleDateString() : "Never"}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button className="btn-action" onClick={() => onManageUser(item)}>
                          Manage
                        </button>
                        <button
                          className="btn-action"
                          title="Reset Password"
                          onClick={() => handleResetPassword(item)}
                        >
                          <Key size={13} />
                        </button>
                        <button
                          className="btn-action"
                          title={item.role === "admin" ? "Demote to Analyst" : "Make Admin"}
                          disabled={item.is_protected || isSelf}
                          onClick={() => handleRoleToggle(item)}
                        >
                          <Shield size={13} />
                        </button>
                        <button
                          className={`btn-action ${item.status === "active" ? "btn-action-warning" : ""}`}
                          title={item.status === "active" ? "Suspend user" : "Activate user"}
                          disabled={item.is_protected || isSelf}
                          onClick={() => handleToggleStatus(item)}
                        >
                          {item.status === "active" ? <Ban size={13} /> : <CheckCircle size={13} />}
                        </button>
                        <button
                          className="btn-action btn-action-danger"
                          title="Delete user"
                          disabled={item.is_protected || isSelf}
                          onClick={() => handleDelete(item)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="pagination-bar">
        <div>
          Showing {users.length > 0 ? (page - 1) * pageSize + 1 : 0} to{" "}
          {Math.min(page * pageSize, total)} of {total} accounts
        </div>
        <div className="pagination-controls">
          <select
            className="select-filter"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>

          <button
            className="page-btn"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft size={16} />
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            className="page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
